import { useEffect, useState } from "react";
import { NavLink, Outlet, useOutletContext, useParams } from "react-router";

import { fetchMyRealm } from "../lib/api";
import { describeApiError } from "../lib/apiError";
import type { Realm } from "../lib/types";

export interface RealmContext {
  realmId: string;
  realm: Realm | null;
  reloadRealm: () => void;
}

export function useRealmContext(): RealmContext {
  return useOutletContext<RealmContext>();
}

// Relative targets so the encoded ':' in the location is never re-encoded.
const TABS = [
  { to: ".", label: "Overview", end: true },
  { to: "llm", label: "Models", end: false },
  { to: "styles", label: "Styles", end: false },
];

/**
 * Owns the realm identity for every sub-route: fetched once here instead of five
 * parallel fetches in one effect, so a tab holding dirty form state is not blown
 * away by a sibling's reload.
 */
export default function RealmLayout() {
  const { realmId = "" } = useParams();
  const [realm, setRealm] = useState<Realm | null>(null);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!realmId) return;
    let live = true;
    setError("");
    fetchMyRealm(realmId)
      .then((r) => {
        if (live) setRealm(r);
      })
      .catch((e: unknown) => {
        if (live) setError(describeApiError(e).message);
      });
    return () => {
      live = false;
    };
  }, [realmId, reloadKey]);

  const ctx: RealmContext = {
    realmId,
    realm,
    reloadRealm: () => setReloadKey((k) => k + 1),
  };

  return (
    <div className="px-8 py-6 space-y-6">
      <div>
        <NavLink to="/realms" className="text-xs text-kw-text-muted hover:text-kw-text-primary">
          ← Realms
        </NavLink>
        <h2 className="text-lg font-medium mt-1">{realm?.display_name ?? realmId}</h2>
        <div className="text-xs text-kw-text-faint font-mono">{realmId}</div>
      </div>

      {error && <div className="text-sm text-red-400">{error}</div>}

      {realm?.status === "tombstoned" && (
        <div className="text-xs text-amber-400 border border-amber-500/50 rounded p-3">
          This realm is tombstoned{realm.tombstoned_at ? ` since ${realm.tombstoned_at}` : ""} —
          reads are refused and edits will not take effect.
        </div>
      )}
      {realm && realm.blocked_packs.length > 0 && (
        <div className="text-xs text-amber-400 border border-amber-500/50 rounded p-3">
          Bound packs are quarantined on this instance:{" "}
          <span className="font-mono">{realm.blocked_packs.join(", ")}</span>. Realm reads stay
          blocked until the operator repairs or unbinds them.
        </div>
      )}

      <nav className="flex gap-1 border-b border-kw-border-subtle">
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) =>
              `px-3 py-2 text-sm border-b-2 -mb-px ${
                isActive
                  ? "border-kw-l1 text-kw-text-primary"
                  : "border-transparent text-kw-text-muted hover:text-kw-text-primary"
              }`
            }
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>

      <Outlet context={ctx} />
    </div>
  );
}
